import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Wrench, Info, PlaneTakeoff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

interface MaintenanceAlertProps {
  isOpen: boolean;
  isClosable?: boolean; // 🟢 Parametrul nou (opțional, implicit true dacă nu e trimis)
  onClose: () => void;
}

const MaintenanceAlert = ({ isOpen, isClosable = true, onClose }: MaintenanceAlertProps) => {
  const { currentLanguageDictionary } = useAuth();

  // Funcție simplă care extrage exclusiv valoarea asociată cheii din dicționar
  const getDictionaryValue = (key: string): string => {
    if (!currentLanguageDictionary) return "";
    const item = currentLanguageDictionary.find((d: any) => d.dictKey === key);
    return item?.dictValue || "";
  };

  // Preluarea textelor strict după cheia din dicționar
  const headerText = isClosable
    ? getDictionaryValue("alert.maintenance.header.dev")
    : getDictionaryValue("alert.maintenance.header.block");

  const titleText = getDictionaryValue("alert.maintenance.title");
  const descriptionText = getDictionaryValue("alert.maintenance.description");
  const item1Text = getDictionaryValue("alert.maintenance.item1");
  const item2Text = getDictionaryValue("alert.maintenance.item2");
  const buttonText = getDictionaryValue("alert.maintenance.button");

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-[8px] p-6">
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="relative w-full max-w-md overflow-hidden rounded-[2.5rem] bg-[#0F172A] border border-orange-500/30 shadow-[0_0_50px_rgba(249,115,22,0.15)]"
          >
            {/* Header Decorativ */}
            <div className="h-2 bg-gradient-to-r from-orange-600 via-yellow-500 to-orange-600" />

            <div className="p-10">
              <div className="flex items-center gap-5 mb-8">
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-orange-500/10 text-orange-500 border border-orange-500/20 shadow-[0_0_20px_rgba(249,115,22,0.2)]">
                  <Wrench size={36} className="animate-pulse" />
                </div>
                <div>
                  <p className="text-[10px] font-black tracking-[0.3em] text-orange-500 uppercase">
                    {headerText}
                  </p>
                  <h3 className="text-2xl font-black text-white italic leading-tight">
                    {titleText}
                  </h3>
                </div>
              </div>

              <div className="space-y-5 mb-10">
                <p className="text-slate-300 text-sm leading-relaxed">
                  {descriptionText}
                </p>

                <div className="rounded-2xl bg-orange-500/5 border border-orange-500/10 p-5">
                  <ul className="space-y-4">
                    <li className="flex gap-4 text-xs text-slate-400">
                      <div className="mt-0.5 shrink-0">
                        <Info size={14} className="text-orange-500" />
                      </div>
                      <span className="leading-tight">
                        {item1Text}
                      </span>
                    </li>
                    <li className="flex gap-4 text-xs text-slate-400">
                      <div className="mt-0.5 shrink-0">
                        <PlaneTakeoff size={14} className="text-orange-500" />
                      </div>
                      <span className="leading-tight">
                        {item2Text}
                      </span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* 🟢 RENDERING CONDIȚIONAL PENTRU BUTON */}
              {isClosable && (
                <div className="flex flex-col gap-3">
                  <button
                    onClick={onClose}
                    className="w-full px-6 py-4 rounded-xl bg-orange-600 text-white text-xs font-black uppercase tracking-widest hover:bg-orange-500 transition-all shadow-[0_0_20px_rgba(249,115,22,0.3)] flex items-center justify-center gap-3 group"
                  >
                    {buttonText}
                  </button>
                </div>
              )}
            </div>

            <div className="absolute -bottom-16 -right-16 opacity-[0.02] pointer-events-none text-white">
              <Wrench size={240} />
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default MaintenanceAlert;